import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, CheckCircle2, Loader2 } from "lucide-react";
import { PageHeader } from "./ServiceDetail";
import { GoldButton } from "../components/ui-kit";
import { api, formatApiError } from "../lib/api";
import { useContent } from "../context/ContentContext";
import { EVENT_TYPES, QUOTE_SERVICES } from "../data/content";
import { toast } from "sonner";

const DRAFT_KEY = "kmr-devis-draft";
const TECH_TRIGGERS = ["Sonorisation", "Éclairage", "Location de matériel", "Installation technique"];

const emptyForm = {
  eventType: "", date: "", dateFlexible: false, startTime: "", endTime: "", guests: "",
  location: "", venueType: "", address: "", city: "", postalCode: "", country: "France",
  services: [], equipmentOnSite: false, needDelivery: false, needInstall: false, needTechnician: false,
  powerAvailable: false, accessConstraints: "", ambiance: "", musicPreferences: "", specialMoments: "",
  projectDescription: "", inspirationLink: "", budget: "", firstName: "", lastName: "", company: "",
  email: "", phone: "", preferredContact: "email", callWindow: "", language: "fr",
  consent: false, newsletter: false, honeypot: "",
};

const inputCls = "w-full bg-input/60 border border-white/10 rounded-xl px-4 py-3 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-gold/60 transition-colors";
const labelCls = "block text-sm text-foreground/80 mb-2";

function Field({ label, children }) {
  return <div><label className={labelCls}>{label}</label>{children}</div>;
}

export default function Devis() {
  const [params] = useSearchParams();
  const { content } = useContent();
  const budgetRanges = content?.budgetRanges || ["Budget à définir"];

  const [form, setForm] = useState(() => {
    const saved = sessionStorage.getItem(DRAFT_KEY);
    const base = saved ? { ...emptyForm, ...JSON.parse(saved) } : emptyForm;
    const t = params.get("type");
    if (t && EVENT_TYPES.find((e) => e.id === t)) base.eventType = t;
    return base;
  });
  const [step, setStep] = useState(form.eventType ? 1 : 0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const showTech = useMemo(() => form.services.some((s) => TECH_TRIGGERS.includes(s)), [form.services]);

  // Build dynamic step list
  const steps = useMemo(() => {
    const base = ["type", "general", "services"];
    if (showTech) base.push("technique");
    base.push("ambiance", "budget", "coordonnees");
    return base;
  }, [showTech]);

  const current = steps[step];
  const progress = Math.round(((step + 1) / steps.length) * 100);

  const canNext = () => {
    if (current === "type") return !!form.eventType;
    if (current === "coordonnees") return form.firstName && form.lastName && form.email && form.phone && form.consent;
    return true;
  };

  const next = () => { setError(""); if (step < steps.length - 1) setStep(step + 1); };
  const back = () => { setError(""); if (step > 0) setStep(step - 1); };

  const submit = async () => {
    if (!canNext()) { setError("Merci de compléter les champs obligatoires et le consentement."); return; }
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/quotes", form);
      setDone(data.request_number);
      sessionStorage.removeItem(DRAFT_KEY);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      toast.error("Envoi impossible", { description: formatApiError(e.response?.data?.detail) });
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="devis-confirmation">
        <PageHeader overline="Demande envoyée" title="Merci, votre demande est bien reçue !" />
        <section className="pb-24"><div className="max-w-2xl mx-auto px-5 sm:px-8">
          <div className="glass rounded-2xl p-10 text-center">
            <CheckCircle2 size={56} className="text-gold mx-auto mb-6" />
            <p className="text-lg text-foreground/85">Nous avons bien reçu votre demande de devis. Notre équipe revient vers vous rapidement avec une proposition personnalisée.</p>
            <div className="mt-6 inline-block glass rounded-xl px-6 py-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Numéro de demande</p>
              <p className="font-serif text-2xl gold-text" data-testid="request-number">{done}</p>
            </div>
            <div className="mt-8"><GoldButton to="/" data-testid="confirmation-home-btn">Retour à l'accueil</GoldButton></div>
          </div>
        </div></section>
      </div>
    );
  }

  return (
    <div data-testid="devis-page">
      <PageHeader overline="Demande de devis" title="Décrivez-nous votre événement" subtitle="Quelques étapes pour recevoir une proposition adaptée à votre projet. Vos réponses sont enregistrées automatiquement." />
      <section className="pb-24">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          {/* progress */}
          <div className="mb-8">
            <div className="flex justify-between text-xs text-muted-foreground mb-2">
              <span>Étape {step + 1} / {steps.length}</span><span>{progress}%</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <motion.div className="h-full bg-gradient-to-r from-gold-light to-gold-dark" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} data-testid="devis-progress" />
            </div>
          </div>

          <div className="glass rounded-2xl p-6 sm:p-9">
            {/* honeypot */}
            <input type="text" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)} className="hidden" aria-hidden="true" />
            <AnimatePresence mode="wait">
              <motion.div key={current} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-5">

                {current === "type" && (
                  <div data-testid="step-type">
                    <h3 className="font-serif text-2xl mb-5">Quel type d'événement ?</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {EVENT_TYPES.map((e) => (
                        <button key={e.id} onClick={() => set("eventType", e.id)} data-testid={`opt-type-${e.id}`}
                          className={`text-left px-5 py-4 rounded-xl border transition-colors ${form.eventType === e.id ? "border-gold bg-gold/10 text-gold" : "border-white/10 hover:border-gold/40 text-foreground/85"}`}>
                          {e.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {current === "general" && (
                  <div data-testid="step-general" className="space-y-5">
                    <h3 className="font-serif text-2xl">Informations générales</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Date de l'événement"><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="input-date" /></Field>
                      <Field label="Nombre estimé d'invités"><input type="number" min="1" value={form.guests} onChange={(e) => set("guests", e.target.value)} placeholder="ex. 80" className={inputCls} data-testid="input-guests" /></Field>
                      <Field label="Heure de début"><input type="time" value={form.startTime} onChange={(e) => set("startTime", e.target.value)} className={inputCls} data-testid="input-start" /></Field>
                      <Field label="Heure de fin estimée"><input type="time" value={form.endTime} onChange={(e) => set("endTime", e.target.value)} className={inputCls} data-testid="input-end" /></Field>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer text-sm text-foreground/80">
                      <input type="checkbox" checked={form.dateFlexible} onChange={(e) => set("dateFlexible", e.target.checked)} className="accent-gold w-4 h-4" data-testid="input-flexible" /> Ma date est flexible
                    </label>
                    <Field label="Type de lieu (intérieur / extérieur / mixte)">
                      <div className="flex flex-wrap gap-3">
                        {["Intérieur", "Extérieur", "Mixte"].map((l) => (
                          <button key={l} onClick={() => set("location", l)} className={`px-4 py-2 rounded-full text-sm border ${form.location === l ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-foreground/80"}`} data-testid={`opt-loc-${l}`}>{l}</button>
                        ))}
                      </div>
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Ville"><input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Ville de l'événement" className={inputCls} data-testid="input-city" /></Field>
                      <Field label="Code postal"><input value={form.postalCode} onChange={(e) => set("postalCode", e.target.value)} placeholder="Code postal" className={inputCls} data-testid="input-postal" /></Field>
                    </div>
                    <Field label="Type de lieu / salle (facultatif)"><input value={form.venueType} onChange={(e) => set("venueType", e.target.value)} placeholder="ex. salle des fêtes, domaine, restaurant…" className={inputCls} data-testid="input-venue" /></Field>
                  </div>
                )}

                {current === "services" && (
                  <div data-testid="step-services">
                    <h3 className="font-serif text-2xl mb-2">Prestations souhaitées</h3>
                    <p className="text-sm text-muted-foreground mb-5">Sélectionnez tout ce qui vous intéresse.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {QUOTE_SERVICES.map((s) => (
                        <button key={s} onClick={() => toggleService(s)} data-testid={`opt-service-${s}`}
                          className={`flex items-center gap-3 text-left px-4 py-3 rounded-xl border transition-colors ${form.services.includes(s) ? "border-gold bg-gold/10 text-gold" : "border-white/10 hover:border-gold/40 text-foreground/85"}`}>
                          <span className={`w-5 h-5 rounded-md border flex items-center justify-center ${form.services.includes(s) ? "bg-gold border-gold" : "border-white/30"}`}>{form.services.includes(s) && <Check size={13} className="text-background" />}</span>
                          <span className="text-sm">{s}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {current === "technique" && (
                  <div data-testid="step-technique" className="space-y-4">
                    <h3 className="font-serif text-2xl">Informations techniques</h3>
                    <p className="text-sm text-muted-foreground">Ces précisions nous aident à préparer la logistique.</p>
                    {[
                      ["equipmentOnSite", "Du matériel est déjà disponible sur place"],
                      ["needDelivery", "J'ai besoin d'une livraison"],
                      ["needInstall", "J'ai besoin d'une installation"],
                      ["needTechnician", "J'ai besoin d'un technicien sur place"],
                      ["powerAvailable", "Une alimentation électrique est disponible"],
                    ].map(([k, l]) => (
                      <label key={k} className="flex items-center gap-3 cursor-pointer text-sm text-foreground/85 glass rounded-xl px-4 py-3">
                        <input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="accent-gold w-4 h-4" data-testid={`input-${k}`} /> {l}
                      </label>
                    ))}
                    <Field label="Contraintes d'accès (étage, ascenseur, stationnement, horaires d'installation/démontage, contraintes sonores…)">
                      <textarea rows={3} value={form.accessConstraints} onChange={(e) => set("accessConstraints", e.target.value)} className={inputCls} data-testid="input-access" />
                    </Field>
                  </div>
                )}

                {current === "ambiance" && (
                  <div data-testid="step-ambiance" className="space-y-4">
                    <h3 className="font-serif text-2xl">Ambiance & attentes</h3>
                    <Field label="Style ou ambiance recherchée"><input value={form.ambiance} onChange={(e) => set("ambiance", e.target.value)} placeholder="ex. élégant, festif, intimiste…" className={inputCls} data-testid="input-ambiance" /></Field>
                    <Field label="Préférences musicales"><input value={form.musicPreferences} onChange={(e) => set("musicPreferences", e.target.value)} placeholder="Genres, artistes, morceaux…" className={inputCls} data-testid="input-music" /></Field>
                    <Field label="Moments particuliers à accompagner"><input value={form.specialMoments} onChange={(e) => set("specialMoments", e.target.value)} placeholder="Entrée, première danse, discours…" className={inputCls} data-testid="input-moments" /></Field>
                    <Field label="Décrivez votre projet"><textarea rows={4} value={form.projectDescription} onChange={(e) => set("projectDescription", e.target.value)} placeholder="Parlez-nous de votre événement…" className={inputCls} data-testid="input-description" /></Field>
                    <Field label="Lien d'inspiration (facultatif)"><input value={form.inspirationLink} onChange={(e) => set("inspirationLink", e.target.value)} placeholder="Salle, tableau d'ambiance…" className={inputCls} data-testid="input-inspiration" /></Field>
                  </div>
                )}

                {current === "budget" && (
                  <div data-testid="step-budget">
                    <h3 className="font-serif text-2xl mb-2">Votre budget</h3>
                    <p className="text-sm text-muted-foreground mb-5">Cette information nous aide à vous proposer une solution adaptée. Elle n'est pas bloquante.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {budgetRanges.map((b) => (
                        <button key={b} onClick={() => set("budget", b)} data-testid={`opt-budget-${b}`}
                          className={`px-5 py-4 rounded-xl border text-left transition-colors ${form.budget === b ? "border-gold bg-gold/10 text-gold" : "border-white/10 hover:border-gold/40 text-foreground/85"}`}>{b}</button>
                      ))}
                    </div>
                  </div>
                )}

                {current === "coordonnees" && (
                  <div data-testid="step-coordonnees" className="space-y-4">
                    <h3 className="font-serif text-2xl">Vos coordonnées</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Prénom *"><input value={form.firstName} onChange={(e) => set("firstName", e.target.value)} className={inputCls} data-testid="input-firstname" /></Field>
                      <Field label="Nom *"><input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} className={inputCls} data-testid="input-lastname" /></Field>
                      <Field label="E-mail *"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="input-email" /></Field>
                      <Field label="Téléphone *"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="input-phone" /></Field>
                      <Field label="Entreprise / organisation"><input value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="input-company" /></Field>
                      <Field label="Créneau de rappel préféré"><input value={form.callWindow} onChange={(e) => set("callWindow", e.target.value)} placeholder="ex. en soirée" className={inputCls} data-testid="input-callwindow" /></Field>
                    </div>
                    <Field label="Moyen de contact préféré">
                      <div className="flex gap-3">
                        {["email", "téléphone", "WhatsApp"].map((m) => (
                          <button key={m} onClick={() => set("preferredContact", m)} className={`px-4 py-2 rounded-full text-sm border capitalize ${form.preferredContact === m ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-foreground/80"}`} data-testid={`opt-contact-${m}`}>{m}</button>
                        ))}
                      </div>
                    </Field>
                    <label className="flex items-start gap-3 cursor-pointer text-sm text-foreground/85 glass rounded-xl px-4 py-3">
                      <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="accent-gold w-4 h-4 mt-0.5" data-testid="input-consent" />
                      <span>J'accepte que mes informations soient utilisées pour traiter ma demande de devis (obligatoire). *</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer text-sm text-foreground/70 px-4">
                      <input type="checkbox" checked={form.newsletter} onChange={(e) => set("newsletter", e.target.checked)} className="accent-gold w-4 h-4 mt-0.5" data-testid="input-newsletter" />
                      <span>Je souhaite recevoir les actualités de KMR Event (facultatif).</span>
                    </label>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {error && <p className="text-destructive text-sm mt-4" data-testid="devis-error">{error}</p>}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
              <button onClick={back} disabled={step === 0} data-testid="devis-back" className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ArrowLeft size={16} /> Précédent
              </button>
              {step < steps.length - 1 ? (
                <GoldButton onClick={() => { canNext() ? next() : setError("Merci de compléter les champs requis."); }} data-testid="devis-next">Suivant <ArrowRight size={16} /></GoldButton>
              ) : (
                <GoldButton onClick={submit} data-testid="devis-submit" disabled={submitting || !form.consent} className={!form.consent ? "opacity-50 cursor-not-allowed" : ""}>{submitting ? <><Loader2 size={16} className="animate-spin" /> Envoi…</> : <>Envoyer ma demande <Check size={16} /></>}</GoldButton>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
